import Swal, { SweetAlertResult } from 'sweetalert2';
import './popup.css';

/*
 * Reusable BeyondBox popup, built on sweetalert2.
 *
 *   import { showPopup } from '../../common_components/popup/popup';
 *
 *   showPopup({
 *       variant: 'success',
 *       icon: 'check',
 *       title: 'Box saved',
 *       text: 'Your box has been saved.',
 *       confirmText: 'Done',
 *   }).then((result) => { if (result.isConfirmed) { ... } });
 */

export const popupIcons = {
    shield: `
        <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <path d="M9 12l2 2 4-4"/>
        </svg>`,
    refresh: `
        <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-2.64-6.36"/>
            <path d="M21 3v6h-6"/>
        </svg>`,
    lock: `
        <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="4" y="11" width="16" height="10" rx="2"/>
            <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
        </svg>`,
    check: `
        <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M5 12.5l4.5 4.5L19 7.5"/>
        </svg>`,
    info: `
        <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9"/>
            <path d="M12 11v5"/>
            <path d="M12 7.5h.01"/>
        </svg>`,
    alert: `
        <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M10.3 3.9L2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/>
            <path d="M12 9v4"/>
            <path d="M12 17h.01"/>
        </svg>`,
};

export type PopupVariant = 'warning' | 'error' | 'success' | 'info';
export type PopupIcon = keyof typeof popupIcons;

export interface PopupOptions {
    variant?: PopupVariant;
    // One of the built-in icons, or a raw SVG string.
    icon?: PopupIcon | string;
    title: string;
    // Plain text (escaped for you). Use `html` instead when you need markup.
    text?: string;
    // Trusted markup only: never pass unescaped user / API input here.
    html?: string;
    confirmText?: string;
    cancelText?: string;
    // false = user must press the confirm button (no X, Escape or outside click).
    dismissible?: boolean;
}

export const escapeHtml = (value: string) =>
    value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

const defaultIcon: Record<PopupVariant, PopupIcon> = {
    warning: 'alert',
    error: 'alert',
    success: 'check',
    info: 'info',
};

export const showPopup = ({
    variant = 'info',
    icon,
    title,
    text,
    html,
    confirmText = 'OK',
    cancelText,
    dismissible = true,
}: PopupOptions): Promise<SweetAlertResult> => {
    const iconName = icon || defaultIcon[variant];
    const body = html !== undefined ? html : `<p>${escapeHtml(text || '')}</p>`;

    return Swal.fire({
        iconHtml: popupIcons[iconName as PopupIcon] || iconName,
        title,
        html: body,
        confirmButtonText: confirmText,
        showCancelButton: !!cancelText,
        cancelButtonText: cancelText,
        showCloseButton: dismissible,
        allowOutsideClick: dismissible,
        allowEscapeKey: dismissible,
        buttonsStyling: false,
        reverseButtons: true,
        returnFocus: false,
        showClass: { popup: 'bb-popup-show' },
        hideClass: { popup: 'bb-popup-hide' },
        customClass: {
            container: 'bb-popup-backdrop',
            popup: `bb-popup bb-popup--${variant}`,
            icon: 'bb-popup-icon',
            title: 'bb-popup-title',
            htmlContainer: 'bb-popup-text',
            actions: 'bb-popup-actions',
            confirmButton: 'bb-popup-btn bb-popup-btn--primary',
            cancelButton: 'bb-popup-btn bb-popup-btn--ghost',
            closeButton: 'bb-popup-close',
        },
    });
};
